// client/src/pages/ShopPOS/components/CartItem.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus, Trash2 } from "lucide-react";

interface CartItemProps {
  cartItem: {
    inventoryId: string;
    quantity: number;
    surchargePercentage?: number;
  };
  item: any;
  product: any;
  getCurrencySymbol: () => string;
  onUpdateQuantity: (id: string, delta: number) => void;
  onUpdateSurcharge: (id: string, percentage: number) => void;
  onRemove: (id: string) => void;
  isLoading?: boolean;
}

export function CartItem({
  cartItem,
  item,
  product,
  getCurrencySymbol,
  onUpdateQuantity,
  onUpdateSurcharge,
  onRemove,
  isLoading = false,
}: CartItemProps) {
  const subtotal = Number(item.price) * cartItem.quantity;
  const surchargePercentage = cartItem.surchargePercentage || 0;
  const surchargeAmount = subtotal * (surchargePercentage / 100);
  const totalWithSurcharge = subtotal + surchargeAmount;

  // Mostrar placeholder durante carga
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 bg-card border p-3 rounded-lg shadow-sm opacity-50">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="h-4 bg-muted rounded w-24 animate-pulse mb-2"></div>
            <div className="h-3 bg-muted rounded w-16 animate-pulse"></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled>
              <Minus className="h-3 w-3" />
            </Button>
            <span className="w-6 text-center font-bold">
              {cartItem.quantity}
            </span>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onRemove(cartItem.inventoryId)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 bg-card border p-3 rounded-lg shadow-sm"
      data-testid={`cart-item-${cartItem.inventoryId}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="font-bold text-sm">{product.name}</div>
          <div className="text-muted-foreground text-xs">
            {getCurrencySymbol()}
            {item.price} x {cartItem.quantity}
            {surchargePercentage > 0 && (
              <span className="ml-2 text-amber-600">
                +{surchargePercentage}% recargo
              </span>
            )}
          </div>
          <div className="text-xs font-medium mt-1">
            Subtotal: {getCurrencySymbol()}
            {subtotal.toFixed(2)}
            {surchargePercentage > 0 && (
              <span className="text-amber-600">
                {" "}
                → Total: {getCurrencySymbol()}
                {totalWithSurcharge.toFixed(2)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onUpdateQuantity(cartItem.inventoryId, -1)}
            data-testid={`button-decrease-${cartItem.inventoryId}`}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-6 text-center font-bold">{cartItem.quantity}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onUpdateQuantity(cartItem.inventoryId, 1)}
            data-testid={`button-increase-${cartItem.inventoryId}`}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={() => onRemove(cartItem.inventoryId)}
          data-testid={`button-remove-${cartItem.inventoryId}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Campo de recargo por producto */}
      <div className="flex items-center gap-2 pt-1 border-t">
        <span className="text-xs text-muted-foreground">Recargo:</span>
        <Input
          type="number"
          min="0"
          max="100"
          step="1"
          value={surchargePercentage}
          onFocus={(e) => {
            if (e.target.value === "0") {
              e.target.value = "";
            }
          }}
          onChange={(e) => {
            const val = parseInt(e.target.value) || 0;
            onUpdateSurcharge(cartItem.inventoryId, val);
          }}
          className="w-20 h-8 text-sm text-center"
          placeholder="0"
        />
        <span className="text-xs text-muted-foreground">%</span>
        {surchargePercentage > 0 && (
          <span className="text-xs text-green-600 ml-auto">
            +{getCurrencySymbol()}
            {surchargeAmount.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
}
